import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "../../api/api";
import toast from "react-hot-toast";
import { set_user_image } from "../../store/reducers/authReducer";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { countries, currencyMapping } from "../../utils/countries";
import { Camera, Trash2, User } from "lucide-react";

const UpdateAccountInfo = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});
  const { userInfo, profileImage: reduxProfileImage } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [profileImage, setProfileImage] = useState(reduxProfileImage || '');
  const [imageUploading, setImageUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const imageInputRef = useRef(null);

  // State for form fields
  const [state, setState] = useState({
    country: "",
    currency: "",
    shippingDetails: {
      name: "",
      address: "",
      phone: "",
      postal: "",
      province: "",
      city: "",
      area: "",
      country: "",
    },
  });

  // Fetch user details from the backend
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const { data } = await axios.get("/account/get-details", {
          withCredentials: true,
        });
        
        setState({
          country: data.country || "",
          currency: data.currency || "",
          shippingDetails: {
            name: data.shippingDetails?.name || "",
            address: data.shippingDetails?.address || "",
            phone: data.shippingDetails?.phone || "",
            postal: data.shippingDetails?.postal || "",
            province: data.shippingDetails?.province || "",
            city: data.shippingDetails?.city || "",
            area: data.shippingDetails?.area || "",
            country: data.shippingDetails?.country || "",
          },
        });
        if (data.image) setProfileImage(data.image);
      } catch (error) {
        toast.error("Failed to fetch user details");
      } finally {
        setFetching(false);
      }
    };

    fetchUserDetails();
  }, []);

  // Handle input changes
  const inputHandle = (e) => {
    const { name, value } = e.target;
    if (name in state.shippingDetails) {
      setState({
        ...state,
        shippingDetails: {
          ...state.shippingDetails,
          [name]: value,
        },
      });
    } else {
      setState({
        ...state,
        [name]: value,
      });
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  // Handle select changes
  const handleSelectChange = (value, fieldName) => {
    if (fieldName === "shippingCountry") {
      setState((prev) => ({
        ...prev,
        shippingDetails: {
          ...prev.shippingDetails,
          country: value,
        },
      }));
    } else {
      setState((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    }

    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  // Handle profile image selection and upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    // Preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setProfileImage(ev.target.result);
    reader.readAsDataURL(file);

    // Upload to backend
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await axios.post('/account/upload-image', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileImage(data.image);
      dispatch(set_user_image(data.image));
      toast.success('Profile photo updated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Please enter your password to confirm deletion');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.delete('/account/delete', {
        data: { password: deletePassword },
        withCredentials: true
      });
      toast.success('Account deleted');
      localStorage.removeItem('customerToken');
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (!state.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!state.currency.trim()) {
      newErrors.currency = "Currency is required";
    }

    if (!state.shippingDetails.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!state.shippingDetails.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!state.shippingDetails.phone.trim()) {
      newErrors.phone = "Phone is required";
    }

    if (!state.shippingDetails.postal.trim()) {
      newErrors.postal = "Postal code is required";
    }

    if (!state.shippingDetails.province.trim()) {
      newErrors.province = "Province is required";
    }

    if (!state.shippingDetails.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!state.shippingDetails.area.trim()) {
      newErrors.area = "Area is required";
    }

    if (!state.shippingDetails.country.trim()) {
      newErrors.shippingCountry = "Shipping country is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.put(
        "/account/update-info",
        {
          country: state.country,
          currency: state.currency,
          shippingDetails: state.shippingDetails,
        },
        { withCredentials: true }
      );

      toast.success(data.message);

      const { data: userData } = await axios.get("/account/get-details", {
        withCredentials: true,
      });

      setState({
        country: userData.country || "",
        currency: userData.currency || "",
        shippingDetails: {
          name: userData.shippingDetails?.name || "",
          address: userData.shippingDetails?.address || "",
          phone: userData.shippingDetails?.phone || "",
          postal: userData.shippingDetails?.postal || "",
          province: userData.shippingDetails?.province || "",
          city: userData.shippingDetails?.city || "",
          area: userData.shippingDetails?.area || "",
          country: userData.shippingDetails?.country || "",
        },
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-7 py-5">
      <div className="max-w-[1600px] mx-auto">
        <h1 className="text-[24px] font-bold py-5 text-gray-900">
          Update Account Information
        </h1>

        <Card className="bg-white border border-gray-100 shadow-sm">
          <CardHeader>
            <CardTitle>Account Details</CardTitle>
            <CardDescription className="text-gray-600">
              Update your country, currency, and shipping details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Profile Image */}
            <div className="flex items-center gap-5 mb-6 pb-6 border-b border-gray-100">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User size={32} className="text-gray-400" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={imageUploading}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  {imageUploading ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera size={13} />
                  )}
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{userInfo?.name || 'Your Name'}</p>
                <p className="text-xs text-gray-500 mt-0.5">{userInfo?.email || ''}</p>
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="text-xs text-blue-600 hover:underline mt-1"
                >
                  Change photo
                </button>
              </div>
            </div>
            {fetching ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Country */}
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={state.country}
                    onValueChange={(value) => handleSelectChange(value, "country")}
                  >
                    <SelectTrigger id="country" className="w-full px-3 py-2 mt-2 bg-transparent h-10 focus-visible:border-foreground border rounded-lg">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-700">
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.name} className="hover:bg-gray-100">
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.country && (
                    <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                  )}
                </div>

                {/* Currency */}
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={state.currency}
                    onValueChange={(value) => handleSelectChange(value, "currency")}
                  >
                    <SelectTrigger id="currency" className="w-full px-3 py-2 mt-2 bg-transparent h-10 focus-visible:border-foreground border rounded-lg">
                      <SelectValue placeholder="Select your currency" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-700">
                      {Object.entries(currencyMapping).map(([code, name]) => (
                        <SelectItem key={code} value={code} className="hover:bg-gray-100">
                          {name} ({code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.currency && (
                    <p className="text-red-500 text-sm mt-1">{errors.currency}</p>
                  )}
                </div>

                {/* Shipping Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Shipping Details</h3>

                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={state.shippingDetails.name}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.name ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      type="text"
                      id="address"
                      name="address"
                      value={state.shippingDetails.address}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.address ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your address"
                    />
                    {errors.address && (
                      <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      type="text"
                      id="phone"
                      name="phone"
                      value={state.shippingDetails.phone}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.phone ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  {/* Postal Code */}
                  <div className="space-y-2">
                    <Label htmlFor="postal">Postal Code</Label>
                    <Input
                      type="text"
                      id="postal"
                      name="postal"
                      value={state.shippingDetails.postal}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.postal ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your postal code"
                    />
                    {errors.postal && (
                      <p className="text-red-500 text-sm mt-1">{errors.postal}</p>
                    )}
                  </div>

                  {/* Province */}
                  <div className="space-y-2">
                    <Label htmlFor="province">Province</Label>
                    <Input
                      type="text"
                      id="province"
                      name="province"
                      value={state.shippingDetails.province}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.province ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your province"
                    />
                    {errors.province && (
                      <p className="text-red-500 text-sm mt-1">{errors.province}</p>
                    )}
                  </div>

                  {/* City */}
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      type="text"
                      id="city"
                      name="city"
                      value={state.shippingDetails.city}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.city ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your city"
                    />
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>

                  {/* Area */}
                  <div className="space-y-2">
                    <Label htmlFor="area">Area</Label>
                    <Input
                      type="text"
                      id="area"
                      name="area"
                      value={state.shippingDetails.area}
                      onChange={inputHandle}
                      className={`w-full px-3 py-2 mt-2 bg-transparent focus-visible:border-foreground ${
                        errors.area ? "border-red-500" : ""
                      }`}
                      placeholder="Enter your area"
                    />
                    {errors.area && (
                      <p className="text-red-500 text-sm mt-1">{errors.area}</p>
                    )}
                  </div>

                  {/* Shipping Country */}
                  <div className="space-y-2">
                    <Label htmlFor="shippingCountry">Shipping Country</Label>
                    <Select
                      value={state.shippingDetails.country}
                      onValueChange={(value) => handleSelectChange(value, "shippingCountry")}
                    >
        <SelectTrigger id="shippingCountry" className="w-full px-3 py-2 mt-2 bg-transparent h-10 focus-visible:border-foreground border rounded-lg">
                        <SelectValue placeholder="Select shipping country" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.name} className="hover:bg-gray-100">
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.shippingCountry && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.shippingCountry}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-white text-black hover:bg-gray-200"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Update Information"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        {/* Danger Zone - Delete Account */}
        <Card className="bg-white border border-red-100 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-red-600">Danger Zone</CardTitle>
            <CardDescription className="text-gray-600">
              Permanently delete your account and all associated data. This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
            >
              <Trash2 size={16} className="mr-2" /> Delete My Account
            </Button>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <>
            <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setShowDeleteDialog(false)} />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Delete Account</h2>
              <p className="text-sm text-gray-500 mb-4">
                This will permanently delete your account, order history, and all personal data. Enter your password to confirm.
              </p>
              <div className="space-y-3">
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="focus-visible:border-red-400"
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePassword(''); }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleteLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : 'Delete Account'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UpdateAccountInfo;